import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { formatMoney, maskMoneyInput, parseInputToApi, moneyToInput } from '@/lib/money';
import { Modal, Field } from '@/features/transactions/TransactionForm';
import type { ReferenceData } from '@/features/transactions/types';

export type EditableCard = {
  id: number;
  name: string;
  brand: string | null;
  last4: string | null;
  color: string | null;
  entity_id: number;
  closing_day: number;
  due_day: number;
  credit_limit: string;
  revolving_rate_monthly?: string | null;
};

const BRANDS = [
  { value: '', label: 'Não informar' },
  { value: 'visa', label: 'Visa' },
  { value: 'mastercard', label: 'Mastercard' },
  { value: 'elo', label: 'Elo' },
  { value: 'amex', label: 'American Express' },
  { value: 'hipercard', label: 'Hipercard' },
  { value: 'outro', label: 'Outra' },
];

const COLORS = ['#1e293b', '#b91c1c', '#c2410c', '#047857', '#1d4ed8', '#6d28d9', '#be185d'];

const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

export function CardForm({
  card,
  reference,
  selectedIds,
  onClose,
  onSaved,
}: {
  card: EditableCard | null;
  reference: ReferenceData;
  selectedIds: number[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = card !== null;

  const [name, setName] = useState(card?.name ?? '');
  const [entityId, setEntityId] = useState(card?.entity_id ?? reference.entities[0]?.id ?? 0);
  const [brand, setBrand] = useState(card?.brand ?? '');
  const [last4, setLast4] = useState(card?.last4 ?? '');
  const [bankId, setBankId] = useState('');
  const [limitMasked, setLimitMasked] = useState(
    card ? moneyToInput(card.credit_limit) : '',
  );
  const [closingDay, setClosingDay] = useState(card?.closing_day ?? 1);
  const [dueDay, setDueDay] = useState(card?.due_day ?? 10);
  const [rate, setRate] = useState(card?.revolving_rate_monthly ?? '');
  const [color, setColor] = useState(card?.color ?? COLORS[0]!);

  const limit = parseInputToApi(limitMasked || '0');

  // Mesma conta que o backend faz (RN-24). Duplicada de propósito: o usuário
  // vê o intervalo enquanto escolhe, em vez de descobrir no erro do servidor.
  const gap = dueDay > closingDay ? dueDay - closingDay : 30 - closingDay + dueDay;
  const gapProblem =
    closingDay === dueDay
      ? 'Fechamento e vencimento não podem cair no mesmo dia.'
      : gap < 3
        ? `Só ${gap} dia(s) entre fechamento e vencimento — o normal é de 7 a 10.`
        : null;

  const cycleChanged =
    isEdit && (closingDay !== card.closing_day || dueDay !== card.due_day);

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name,
        ...(isEdit ? {} : { entity_id: entityId }),
        ...(brand ? { brand } : {}),
        ...(last4 ? { last4 } : {}),
        ...(bankId ? { bank_id: Number(bankId) } : {}),
        credit_limit: limit,
        closing_day: closingDay,
        due_day: dueDay,
        ...(rate ? { revolving_rate_monthly: Number(rate) } : {}),
        color,
      };

      return isEdit
        ? api.patch(`/cards/${card.id}`, payload, { entityIds: selectedIds })
        : api.post(`/cards`, payload, { entityIds: selectedIds });
    },
    onSuccess: (res) => {
      const warning = (res as { meta?: { warning?: string | null } })?.meta?.warning;
      if (warning) window.alert(warning);
      onSaved();
    },
  });

  const error = save.error instanceof ApiError ? save.error : null;
  const canSubmit =
    name.trim().length >= 2 && entityId > 0 && Number(limit) > 0 && gapProblem === null;

  return (
    <Modal title={isEdit ? 'Editar cartão' : 'Novo cartão de crédito'} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }} className="space-y-4">
        {error && (
          <div
            role="alert"
            className="rounded-lg border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300"
          >
            <p>{error.message}</p>
            {error.errors && (
              <ul className="mt-1 list-inside list-disc">
                {Object.entries(error.errors).map(([f, m]) => <li key={f}>{m[0]}</li>)}
              </ul>
            )}
          </div>
        )}

        <Field label="Nome do cartão" required>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            minLength={2}
            autoFocus
            placeholder="Ex.: Nubank Pessoal"
            className="input w-full"
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Entidade" required hint={isEdit ? 'não pode ser alterada' : undefined}>
            <select
              value={entityId}
              onChange={(e) => setEntityId(Number(e.target.value))}
              disabled={isEdit}
              className="input w-full disabled:opacity-60"
            >
              {reference.entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select>
          </Field>
          <Field label="Limite" required>
            <input
              value={limitMasked}
              onChange={(e) => setLimitMasked(maskMoneyInput(e.target.value))}
              required
              inputMode="numeric"
              placeholder="0,00"
              className="input w-full text-right"
            />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Bandeira">
            <select value={brand} onChange={(e) => setBrand(e.target.value)} className="input w-full">
              {BRANDS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </Field>
          <Field label="Final" hint="4 dígitos">
            <input
              value={last4}
              onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
              inputMode="numeric"
              maxLength={4}
              placeholder="1234"
              className="input w-full"
            />
          </Field>
          <Field label="Banco">
            <select value={bankId} onChange={(e) => setBankId(e.target.value)} className="input w-full">
              <option value="">Nenhum</option>
              {reference.banks.map((b) => (
                <option key={b.id} value={b.id}>{b.short_name ?? b.name}</option>
              ))}
            </select>
          </Field>
        </div>

        {/* Os dois campos que mais importam acertar: eles decidem em qual
            fatura cada compra cai (RN-23) e quando o dinheiro sai. */}
        <div className="rounded-lg border border-slate-200 p-3 dark:border-slate-800">
          <p className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Ciclo da fatura
          </p>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Fecha no dia" required>
              <select
                value={closingDay}
                onChange={(e) => setClosingDay(Number(e.target.value))}
                className="input w-full"
              >
                {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
            <Field label="Vence no dia" required>
              <select
                value={dueDay}
                onChange={(e) => setDueDay(Number(e.target.value))}
                className="input w-full"
              >
                {DAYS.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </Field>
          </div>

          {gapProblem ? (
            <p className="mt-2 rounded-lg bg-rose-50 px-3 py-2 text-xs text-rose-800 dark:bg-rose-950/40 dark:text-rose-300">
              {gapProblem} Confira os dias na sua fatura.
            </p>
          ) : (
            <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
              {gap} dias entre fechar e vencer. Compras feitas depois do dia {closingDay} entram na
              fatura do mês seguinte.
              {(closingDay > 28 || dueDay > 28) && (
                <> Em meses curtos, o dia usado é o último do mês.</>
              )}
            </p>
          )}

          {cycleChanged && (
            <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              As faturas já existentes mantêm as compras onde estão — mudar o ciclo vale só para as
              próximas. Recalcular o passado mudaria despesas de meses já fechados.
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Juros do rotativo" hint="% ao mês, opcional">
            <input
              value={rate}
              onChange={(e) => setRate(e.target.value.replace(/[^\d.,]/g, '').replace(',', '.'))}
              inputMode="decimal"
              placeholder="14,99"
              className="input w-full text-right"
            />
          </Field>
          <div>
            <span className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300">
              Cor
            </span>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  style={{ background: c }}
                  className={`h-7 w-7 rounded-lg transition ${
                    color === c
                      ? 'ring-2 ring-slate-900 ring-offset-2 dark:ring-white dark:ring-offset-slate-900'
                      : ''
                  }`}
                  aria-label={`Cor ${c}`}
                />
              ))}
            </div>
          </div>
        </div>

        {Number(limit) > 0 && (
          <p className="rounded-lg bg-slate-50 px-3.5 py-2.5 text-sm dark:bg-slate-800/50">
            Limite: <strong>{formatMoney(limit)}</strong>
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-slate-300 px-4 py-2.5 text-sm font-medium dark:border-slate-700"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={!canSubmit || save.isPending}
            className="flex-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
